import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  useContext,
  useEffect,
  useReducer,
} from 'react';

import appReducer, {
  AppState,
  initialAppState,
} from '../../store/reducers/app-reducer';
import { SystemEnvironment } from '@/constants/systemEnviroment';
import { AppAction } from '@/store/actions/app/action-creators';

// Global object for direct access
export const globaAppContextState = {
  environment: '',
};

type AppDispatch = Dispatch<AppAction>;

const defaultDispatch: AppDispatch = () => {
  throw new Error('AppDispatch was called outside of AppProvider');
};

export const AppContext = createContext<AppState>(initialAppState);
export const AppDispatchContext = createContext<AppDispatch>(defaultDispatch);

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error(
      'useAppContext must be used within a AppProvider. Make sure the component is wrapped in AppProvider.',
    );
  }
  return context;
};

export const useAppDispatchContext = () => {
  const dispatch = useContext(AppDispatchContext);
  if (dispatch === defaultDispatch) {
    throw new Error(
      'useAppDispatchContext must be used within a AppProvider. Make sure the component is wrapped in AppProvider.',
    );
  }
  return dispatch;
};

export const AppProvider = ({
  value,
  children,
}: { value: SystemEnvironment } & PropsWithChildren) => {
  const [state, dispatch] = useReducer(appReducer, {
    ...initialAppState,
    environment: value,
  });

  useEffect(() => {
    globaAppContextState.environment = state.environment;
  }, [state.environment]);

  return (
    <AppContext.Provider value={state}>
      <AppDispatchContext.Provider value={dispatch}>
        {children}
      </AppDispatchContext.Provider>
    </AppContext.Provider>
  );
};

export default AppProvider;
